import * as React from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CardActions,
  CardContent,
  CardMedia,
  styled,
} from "@mui/material";
import { Grid } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { Link } from "react-router-dom";
import { useBreakpoint } from "../hooks/useBreakpoint";

const BootstrapDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    maxWidth: 560,
    width: "100%",
  },
}));

export default function CustomizedDialogs({
  title,
  list1,
  list1Title,
  list2,
  list2Title,
  images,
  href,
}: {
  images: string[];
  title: string;
  list1: string[];
  list1Title: string;
  list2: string[];
  list2Title: string;
  href: string;
}) {
  const [open, setOpen] = React.useState(false);
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const { xs } = useBreakpoint();
  const displayList1 = (
    <ul style={{ margin: 0, paddingLeft: "1rem" }}>
      {list1.map((li, index) => (
        <li key={index}>{li}</li>
      ))}
    </ul>
  );
  const displayList2 = (
    <ul style={{ margin: 0, paddingLeft: "1rem" }}>
      {list2.map((li, index) => (
        <li
          key={index}
          style={li.includes("Coming Soon:") ? { fontStyle: "italic" } : {}}
        >
          {li}
        </li>
      ))}
    </ul>
  );
  return (
    <React.Fragment>
      <Button
        onClick={handleClickOpen}
        sx={{
          "minWidth": 0,
          "p": 0.5,
          "position": "relative",
          "borderRadius": "50%",
          "marginTop": 1,
          "&:hover": {
            borderRadius: "50%",
          },
          "&:focus-visible": {
            backgroundColor: "#b9bec3",
          },
        }}
        aria-label={`Open ${title} help dialog`}
      >
        <HelpOutlineOutlinedIcon
          aria-hidden="false"
          sx={{
            zIndex: 10,
          }}
        />
        <svg height="24" width="24" style={{ marginLeft: -24 }}>
          <circle cx="12" cy="12" r="10" fill="white" />
        </svg>
      </Button>

      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
        sx={{ margin: 0, padding: 0, border: "none" }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Grid
            container
            direction="column"
            sx={{
              width: "100%",
              maxWidth: 560,
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              border: "8px solid #a09771",
              boxShadow: "none",
              backgroundColor: "#fff",
              position: "relative",
            }}
          >
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={() => ({
                "position": "absolute",
                "right": 8,
                "top": 8,
                "color": "#adc7df",
                "zIndex": 5,
                "&:hover": {
                  color: "#ffffff",
                  backgroundColor: "#babbbb",
                },
              })}
            >
              <CloseIcon />
            </IconButton>

            <Grid container direction="column" sx={{ flex: 6 }}>
              <CardContent>
                <DialogTitle sx={{ m: 0, p: 0 }} id="customized-dialog-title">
                  <Typography
                    variant="h2"
                    gutterBottom
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 32,
                    }}
                  >
                    {title}
                  </Typography>
                </DialogTitle>

                <Grid container spacing={2}>
                  <Grid size={{ sm: 6, md: 6, xs: 12 }}>
                    <Typography variant="h3" sx={{ paddingBottom: 0 }}>
                      {list1Title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="primary.main"
                      component="div"
                    >
                      {displayList1}
                      {xs && displayList2}
                    </Typography>
                  </Grid>

                  {!xs && (
                    <Grid size={{ sm: 6, md: 6, xs: 12 }}>
                      <Typography variant="h3" sx={{ paddingBottom: 0 }}>
                        {list2Title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="primary.main"
                        component="div"
                      >
                        {displayList2}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Grid>

            <Grid
              container
              sx={{
                flex: 5,
                backgroundColor: "#d9e8f6",
                borderTop: "1px solid #a09771",
                boxShadow: `
                  inset 0 14px 16px -12px rgba(0, 0, 0, 0.55),
                  inset 0 1px 0 rgba(255, 255, 255, 0.6)
                `,
              }}
              alignItems="center"
              justifyContent="center"
              spacing={2}
              px={2}
            >
              <CardContent sx={{ width: "100%" }}>
                <Typography variant="h3" sx={{ textAlign: "center" }}>
                  Recommended for
                </Typography>

                <Grid
                  container
                  wrap="nowrap"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                    gap: { xs: 2, sm: 4, md: 10 },
                  }}
                >
                  {images.map((img, index) => (
                    <Grid key={index} sx={{ flex: "0 0 auto" }}>
                      <CardMedia
                        component="img"
                        image={img}
                        alt="MediaCardPopover"
                        sx={{
                          maxWidth: 80,
                          borderRadius: 1,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Grid>

            <CardActions
              sx={{
                paddingTop: 3,
                justifyContent: "center",
                backgroundColor: "#1a4480",
                display: "flex",
                pb: 1,
              }}
            >
              <Button
                color="secondary"
                variant="contained"
                id="visit-btn"
                component={Link}
                to={href}
                onClick={handleClose}
                sx={{
                  "borderSpacing": "12px",
                  "width": 150,
                  "display": "flex",
                  "&:hover, &:focus, &:active": {
                    backgroundColor: "#ffffff",
                    color: "#1A4480",
                  },
                }}
              >
                Visit
              </Button>
            </CardActions>
          </Grid>
        </DialogContent>
      </BootstrapDialog>
    </React.Fragment>
  );
}
